import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Wrench, Briefcase, Code, Palette, Home, Truck, 
  GraduationCap, Heart, Music, Camera, Scissors, 
  Utensils, Zap, Globe, Shield, Smartphone, Coffee,
  Car, Hammer, Paintbrush, Leaf, Baby, Dog, 
  Dumbbell, Plane, ShoppingBag, Stethoscope, Scale,
  BookOpen, Lightbulb, Wifi, X, Pencil, Trash2, Plus, Check, GitMerge
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available icons for categories
const AVAILABLE_ICONS = [
  { name: 'Wrench', component: Wrench, label: 'Repair & Maintenance' },
  { name: 'Briefcase', component: Briefcase, label: 'Business' },
  { name: 'Code', component: Code, label: 'Technology' },
  { name: 'Palette', component: Palette, label: 'Design & Art' },
  { name: 'Home', component: Home, label: 'Home Services' },
  { name: 'Truck', component: Truck, label: 'Delivery & Moving' },
  { name: 'GraduationCap', component: GraduationCap, label: 'Education' },
  { name: 'Heart', component: Heart, label: 'Healthcare' },
  { name: 'Music', component: Music, label: 'Music & Entertainment' },
  { name: 'Camera', component: Camera, label: 'Photography' },
  { name: 'Scissors', component: Scissors, label: 'Beauty & Personal Care' },
  { name: 'Utensils', component: Utensils, label: 'Food & Catering' },
  { name: 'Zap', component: Zap, label: 'Electrical' },
  { name: 'Globe', component: Globe, label: 'Travel & Tourism' },
  { name: 'Shield', component: Shield, label: 'Security' },
  { name: 'Smartphone', component: Smartphone, label: 'Mobile & Electronics' },
  { name: 'Coffee', component: Coffee, label: 'Hospitality' },
  { name: 'Car', component: Car, label: 'Automotive' },
  { name: 'Hammer', component: Hammer, label: 'Construction' },
  { name: 'Paintbrush', component: Paintbrush, label: 'Painting' },
  { name: 'Leaf', component: Leaf, label: 'Landscaping' },
  { name: 'Baby', component: Baby, label: 'Childcare' },
  { name: 'Dog', component: Dog, label: 'Pet Services' },
  { name: 'Dumbbell', component: Dumbbell, label: 'Fitness' },
  { name: 'Plane', component: Plane, label: 'Travel' },
  { name: 'ShoppingBag', component: ShoppingBag, label: 'Shopping' },
  { name: 'Stethoscope', component: Stethoscope, label: 'Medical' },
  { name: 'Scale', component: Scale, label: 'Legal' },
  { name: 'BookOpen', component: BookOpen, label: 'Writing & Content' },
  { name: 'Lightbulb', component: Lightbulb, label: 'Consulting' },
  { name: 'Wifi', component: Wifi, label: 'IT & Networking' },
];

const CategoryManager = ({ isOpen, onClose, onCategoryChange, isAdmin = false }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [usage, setUsage] = useState({});
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSources, setMergeSources] = useState([]);
  const [mergeTarget, setMergeTarget] = useState('');
  const [merging, setMerging] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Briefcase',
    icon_type: 'lucide'
  });

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    } else {
      exitMergeMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      if (isAdmin) {
        const u = await axios.get(`${API}/categories/usage`).catch(() => ({ data: {} }));
        setUsage(u.data || {});
      }
    } catch (error) {
      showMessage('error', 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, msg) => {
    if (type === 'success') {
      setSuccess(msg);
      setError('');
    } else {
      setError(msg);
      setSuccess('');
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showMessage('error', 'Category name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData);
        showMessage('success', 'Category updated successfully');
      } else {
        await axios.post(`${API}/categories`, formData);
        showMessage('success', 'Category created successfully');
      }
      
      fetchCategories();
      resetForm();
      if (onCategoryChange) onCategoryChange();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || 'Briefcase',
      icon_type: category.icon_type || 'lucide'
    });
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/categories/${categoryId}`);
      showMessage('success', 'Category deleted successfully');
      fetchCategories();
      if (onCategoryChange) onCategoryChange();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', icon: 'Briefcase', icon_type: 'lucide' });
    setShowForm(false);
    setShowIconPicker(false);
  };

  const usageLabel = (name) => {
    const u = usage[name];
    if (!u) return '0 services';
    return `${u.services} service${u.services === 1 ? '' : 's'}${u.jobs ? ` · ${u.jobs} job${u.jobs === 1 ? '' : 's'}` : ''}`;
  };

  const toggleMergeSource = (id) => {
    setMergeSources(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    if (mergeTarget === id) setMergeTarget('');
  };

  const exitMergeMode = () => {
    setMergeMode(false);
    setMergeSources([]);
    setMergeTarget('');
  };

  const handleMerge = async () => {
    const target = categories.find(c => c.id === mergeTarget);
    const names = categories.filter(c => mergeSources.includes(c.id)).map(c => c.name);
    if (!target || names.length === 0) return;
    if (!window.confirm(`Merge "${names.join('", "')}" into "${target.name}"?\n\nAll their services and jobs will be moved to "${target.name}" and the merged categories will be deleted. This cannot be undone.`)) return;
    setMerging(true);
    try {
      const res = await axios.post(`${API}/categories/merge`, { source_ids: mergeSources, target_id: mergeTarget });
      showMessage('success', `${res.data.message} — ${res.data.services_moved} services and ${res.data.jobs_moved} jobs moved`);
      exitMergeMode();
      fetchCategories();
      if (onCategoryChange) onCategoryChange();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to merge categories');
    } finally {
      setMerging(false);
    }
  };

  const getIconComponent = (iconName) => {
    const iconData = AVAILABLE_ICONS.find(i => i.name === iconName);
    if (iconData) {
      const IconComp = iconData.component;
      return <IconComp className="w-5 h-5" strokeWidth={1.5} />;
    }
    // Fallback for old emoji icons
    if (iconName && iconName.length <= 4) {
      return <span className="text-xl">{iconName}</span>;
    }
    return <Briefcase className="w-5 h-5" strokeWidth={1.5} />;
  };

  const renderCategoryIcon = (category) => {
    if (category.icon_type === 'image' && category.icon) {
      return (
        <img 
          src={category.icon.startsWith('/api') ? `${BACKEND_URL}${category.icon}` : category.icon} 
          alt={category.name}
          className="w-5 h-5 object-cover"
        />
      );
    }
    return getIconComponent(category.icon);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="category-manager-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Manage Categories</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition"
            data-testid="close-category-manager"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Messages */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2">
              <Check className="w-4 h-4" />
              {success}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {!isAdmin && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm" data-testid="category-add-only-note">
              Adding, editing and deleting categories is restricted to administrators to keep the category list clean and avoid duplicates.
            </div>
          )}

          {/* Add/Edit Form */}
          {showForm ? (
            <div className="mb-6 p-5 bg-gray-50 rounded-xl border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full h-11 px-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                      placeholder="Category name"
                      required
                      data-testid="category-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full h-11 px-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                      placeholder="Brief description"
                      data-testid="category-description-input"
                    />
                  </div>
                </div>

                {/* Icon Selection Dropdown */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowIconPicker(!showIconPicker)}
                      className="w-full h-11 px-4 border border-gray-200 rounded-lg flex items-center justify-between bg-white hover:border-gray-300 transition"
                      data-testid="icon-picker-trigger"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-700">
                          {getIconComponent(formData.icon)}
                        </div>
                        <span className="text-gray-700">
                          {AVAILABLE_ICONS.find(i => i.name === formData.icon)?.label || formData.icon}
                        </span>
                      </div>
                      <svg className={`w-5 h-5 text-gray-400 transition ${showIconPicker ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Icon Picker Dropdown */}
                    {showIconPicker && (
                      <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                        <div className="p-2 grid grid-cols-4 gap-1">
                          {AVAILABLE_ICONS.map((iconData) => {
                            const IconComp = iconData.component;
                            const isSelected = formData.icon === iconData.name;
                            return (
                              <button
                                key={iconData.name}
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, icon: iconData.name, icon_type: 'lucide' });
                                  setShowIconPicker(false);
                                }}
                                className={`p-3 rounded-lg flex flex-col items-center gap-1 transition ${
                                  isSelected 
                                    ? 'bg-blue-50 text-blue-600 ring-2 ring-blue-500' 
                                    : 'hover:bg-gray-50 text-gray-700'
                                }`}
                                title={iconData.label}
                                data-testid={`icon-option-${iconData.name}`}
                              >
                                <IconComp className="w-5 h-5" strokeWidth={1.5} />
                                <span className="text-[10px] truncate w-full text-center">{iconData.label.split(' ')[0]}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="h-11 px-6 border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="h-11 px-6 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium transition disabled:opacity-50"
                    data-testid="save-category-btn"
                  >
                    {saving ? 'Saving...' : (editingCategory ? 'Update Category' : 'Add Category')}
                  </button>
                </div>
              </form>
            </div>
          ) : isAdmin ? (
            <div className="mb-6 flex flex-wrap gap-2">
              <button
                onClick={() => setShowForm(true)}
                className="h-11 px-6 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium transition flex items-center gap-2"
                data-testid="add-category-btn"
              >
                <Plus className="w-4 h-4" />
                Add New Category
              </button>
              <button
                onClick={() => (mergeMode ? exitMergeMode() : setMergeMode(true))}
                className={`h-11 px-6 rounded-full font-medium transition flex items-center gap-2 border ${mergeMode ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                data-testid="merge-categories-btn"
              >
                <GitMerge className="w-4 h-4" />
                {mergeMode ? 'Cancel Merge' : 'Merge Duplicates'}
              </button>
            </div>
          ) : null}

          {isAdmin && mergeMode && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl space-y-3" data-testid="merge-panel">
              <p className="text-sm text-orange-800">
                <span className="font-semibold">Step 1:</span> tick the duplicate categories to merge away.{' '}
                <span className="font-semibold">Step 2:</span> pick the category to keep. Their services and jobs move over automatically.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <select
                  value={mergeTarget}
                  onChange={(e) => setMergeTarget(e.target.value)}
                  disabled={mergeSources.length === 0}
                  className="flex-1 h-11 px-3 border border-gray-300 rounded-lg bg-white text-sm disabled:bg-gray-100 disabled:text-gray-400"
                  data-testid="merge-target-select"
                >
                  <option value="">
                    {mergeSources.length === 0 ? 'Select duplicates below first…' : 'Keep which category?'}
                  </option>
                  {categories.filter(c => !mergeSources.includes(c.id)).map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({usageLabel(c.name)})</option>
                  ))}
                </select>
                <button
                  onClick={handleMerge}
                  disabled={merging || !mergeTarget || mergeSources.length === 0}
                  className="h-11 px-5 bg-orange-600 text-white rounded-full hover:bg-orange-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="merge-confirm-btn"
                >
                  <GitMerge className="w-4 h-4" />
                  {merging ? 'Merging…' : `Merge ${mergeSources.length || ''} into selected`}
                </button>
              </div>
            </div>
          )}

          {/* Categories List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-gray-900 mx-auto"></div>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No categories found. Add your first category above.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => mergeMode && toggleMergeSource(category.id)}
                  className={`flex items-center justify-between p-4 bg-white border rounded-xl transition ${
                    mergeMode ? 'cursor-pointer' : ''
                  } ${mergeSources.includes(category.id) ? 'border-orange-400 bg-orange-50/60' : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'}`}
                  data-testid={`category-item-${category.id}`}
                >
                  <div className="flex items-center gap-4">
                    {mergeMode && (
                      <input
                        type="checkbox"
                        checked={mergeSources.includes(category.id)}
                        onChange={() => toggleMergeSource(category.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 accent-orange-600"
                        data-testid={`merge-source-${category.id}`}
                      />
                    )}
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-xl text-gray-700">
                      {renderCategoryIcon(category)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{category.name}</h4>
                      {category.description && (
                        <p className="text-sm text-gray-500">{category.description}</p>
                      )}
                      {isAdmin && (
                        <p className="text-xs text-gray-400 mt-0.5" data-testid={`category-usage-${category.id}`}>{usageLabel(category.name)}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {mergeMode ? null : isAdmin ? (
                      <>
                        <button
                          onClick={() => handleEdit(category)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit"
                          data-testid={`edit-category-${category.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(category.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete"
                          data-testid={`delete-category-${category.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 italic px-2" data-testid={`category-locked-${category.id}`}>
                        Protected
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;
